import { isPast } from 'date-fns';
import { useEffect, useState, useMemo } from 'react';
import { selectSignedIn, setSessionTime } from 'reducers/auth';
import { useSelector } from 'react-redux';
import { store } from '../index';

export type TokenResource = {
  accessToken: string;
  refreshToken: string;
  refreshTokenExpires: Date;
  tokenExpires: Date;
  scopes?: Scopes;
};

/**
 * Save token data in local storage.
 */
export function saveTokenResource(resource: TokenResource): void {
  const expiry = resource.tokenExpires;
  const now = new Date();
  console.log('expiry: ', expiry);
  const sessionExpiry = expiry.getTime() - now.getTime();

  if (window) {
    console.log('saveTokenResource resource: ', resource);
    console.log('saveTokenResource sessionExpiry: ', sessionExpiry);
    store.dispatch(setSessionTime(sessionExpiry));
    window.localStorage.setItem('AT', resource.accessToken);
    window.localStorage.setItem('ATE', resource.tokenExpires.toString());
    window.localStorage.setItem('RT', resource.refreshToken);
    window.localStorage.setItem('RTE', resource.refreshTokenExpires.toString());
    if (resource.scopes) {
      window.localStorage.setItem('SCOPES', JSON.stringify(resource.scopes));
    }
  }
}

export type LoadedTokenResource = {
  accessToken: string;
  accessTokenExpires: Date;
  refreshToken: string;
  refreshTokenExpires: Date;
  scopes?: string;
};

/**
 * Fetch token data from local storage.
 */
export function loadTokenResource(): null | LoadedTokenResource {
  try {
    const accessToken = window.localStorage.getItem('AT');
    const accessTokenExpiry = window.localStorage.getItem('ATE');
    const refreshToken = window.localStorage.getItem('RT');
    const refreshTokenExpiry = window.localStorage.getItem('RTE');
    const scopes = window.localStorage.getItem('SCOPES');

    // If anything's missing, abort.
    if (
      accessToken == null ||
      refreshToken == null ||
      accessTokenExpiry == null ||
      refreshTokenExpiry == null
    )
      throw new Error();

    const accessTokenExpires = new Date(accessTokenExpiry);
    const refreshTokenExpires = new Date(refreshTokenExpiry);

    if (isPast(refreshTokenExpires)) throw new Error();

    return {
      accessToken,
      accessTokenExpires,
      refreshToken,
      refreshTokenExpires,
      scopes: scopes ?? '[]',
    };
  } catch (_error) {
    return null;
  }
}

export function saveIsAnonymousCandidate(value: boolean): void {
  if (window) {
    window.localStorage.setItem('IS_ANONYMOUS', JSON.stringify(value));
  }
}

export function loadIsAnonymousCandidate(): boolean {
  try {
    return window.localStorage.getItem('IS_ANONYMOUS') === 'true';
  } catch (_error) {
    return false;
  }
}

export type AccessElement = {
  displayName: string;
  path: string;
  childElements?: Scopes;
};

export type Scopes = AccessElement[];

export function loadScopes(): string {
  try {
    const scopes = window.localStorage.getItem('SCOPES');
    if (scopes == null) throw new Error();

    return scopes;
  } catch (error) {
    return '[]';
  }
}

/**
 * Used to load navigation routes that are available to this user.
 * Effect runs on app load and sign-in.
 */
export function useLoadScopes(): Scopes {
  const [scopeString, setScopeString] = useState('[]');
  const isSignedIn = useSelector(selectSignedIn);

  useEffect(() => {
    if (isSignedIn) setScopeString(loadScopes());
  }, [isSignedIn]);

  const scopes: Scopes = useMemo(() => JSON.parse(scopeString), [scopeString]);

  return scopes;
}
